/**
 * What a project costs, from vfs-service's FinOps model (src/finops, in the pod that holds the project), with the
 * person's authorized fetch: `GET /api/v1/projects/:id/finops?runtime=&region=&scenario=&requestsPerDay=&minReplicas=
 * &maxReplicas=&busyHours=&aiCallsPerRequest=&aiPricePerCall=&hoursPerDay=` -> `{ currency: { code, symbol },
 * pricesAsOf, sizing, params: { runtime, region, traffic, hoursPerDay }, scenario, flow: { rates, rows, running, total,
 * perHour, perMonth }, ai, production: { components, byCategory, total, perThousand, requestsPerMonth, runtime, region },
 * scenarios, assumptions, choices: { runtimes, regions, scenarios, categories }, defaultSize, counts, inputs }`. Every
 * choice left out is the Flow admin's default (Settings › FinOps). The same numbers the vfs_finops AI tool gives.
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { buildAclContextHeaders } from '../utils/aclContext';

const base = () => (config.vfsServiceUrl || '').replace(/\/$/, '');
const PARAMS = ['runtime', 'region', 'scenario', 'requestsPerDay', 'minReplicas', 'maxReplicas', 'busyHours', 'aiCallsPerRequest', 'aiPricePerCall', 'hoursPerDay'];

/** The project's FinOps answer for `params`, or null where vfs-service has no FinOps route (an older one). Throws on any other failure. */
export async function getFinOps(projectId, params = {}) {
  if (!base() || !projectId) { return null; }
  const query = new URLSearchParams();
  PARAMS.forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== '') { query.set(key, String(value)); }
  });
  const qs = query.toString();
  const response = await getAuthorizedFetch()(`${base()}/api/v1/projects/${encodeURIComponent(projectId)}/finops${qs ? `?${qs}` : ''}`, {
    headers: { Accept: 'application/json', ...buildAclContextHeaders() },
  });
  if (response.status === 404) { return null; }
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const error = new Error(data?.error || `Request failed (${response.status})`);
    error.status = response.status;
    throw error;
  }
  return data && typeof data === 'object' ? data : null;
}
